import { useEffect, useRef, useState } from "react";

import { jobsApi } from "../api/applications";
import type { Job, JobStep, StepStatus } from "../api/types";

type ServerEvent = {
  type: "step" | "delta" | "job" | "error";
  payload: Record<string, unknown>;
};

export type JobProgress = {
  job: Job | null;
  /** Live text for the step currently generating, for a preview pane. */
  streaming: { step: string; text: string } | null;
  error: string | null;
  finished: boolean;
};

/**
 * Follows a generation job over Server-Sent Events.
 *
 * The stream opens with a `snapshot` replaying the persisted step rows, so a
 * reconnect (or a page reload mid-generation) picks up exactly where things
 * stand rather than showing an empty list. EventSource reconnects by itself,
 * and because every reconnect replays, dropped connections are cosmetic.
 *
 * Authentication rides on the session cookie: EventSource cannot set headers,
 * which is why the whole app uses cookie auth.
 */
export function useJobProgress(jobId: string | null): JobProgress {
  const [job, setJob] = useState<Job | null>(null);
  const [streaming, setStreaming] = useState<{ step: string; text: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);
  const sourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!jobId) {
      setJob(null);
      setStreaming(null);
      setError(null);
      setFinished(false);
      return;
    }

    let cancelled = false;
    const source = new EventSource(jobsApi.eventsUrl(jobId));
    sourceRef.current = source;

    const updateStep = (name: string, changes: Partial<JobStep>) =>
      setJob((current) =>
        current
          ? {
              ...current,
              steps: current.steps.map((step) =>
                step.name === name ? { ...step, ...changes } : step,
              ),
            }
          : current,
      );

    source.addEventListener("snapshot", (event) => {
      if (cancelled) return;
      const snapshot = JSON.parse((event as MessageEvent).data) as Job;
      setJob(snapshot);
      setError(snapshot.error);
      if (["done", "failed", "cancelled"].includes(snapshot.status)) {
        setFinished(true);
        source.close();
      }
    });

    source.addEventListener("step", (event) => {
      if (cancelled) return;
      const { payload } = JSON.parse((event as MessageEvent).data) as ServerEvent;
      const name = payload.name as string;
      updateStep(name, { status: payload.status as StepStatus });
      if (payload.status !== "running") setStreaming(null);
    });

    source.addEventListener("delta", (event) => {
      if (cancelled) return;
      const { payload } = JSON.parse((event as MessageEvent).data) as ServerEvent;
      if (payload.channel !== "text") return;
      const name = payload.name as string;
      setStreaming((current) =>
        current && current.step === name
          ? { step: name, text: current.text + (payload.text as string) }
          : { step: name, text: payload.text as string },
      );
    });

    source.addEventListener("job", (event) => {
      if (cancelled) return;
      const { payload } = JSON.parse((event as MessageEvent).data) as ServerEvent;
      const status = payload.status as Job["status"];
      setJob((current) => (current ? { ...current, status } : current));
      if (["done", "failed", "cancelled"].includes(status)) {
        setFinished(true);
        setStreaming(null);
        source.close();
      }
    });

    source.addEventListener("error", (event) => {
      // Two different things arrive here: an application-level error event
      // carrying a message, and EventSource's own transport error, which has
      // no data and which the browser retries on its own.
      const data = (event as MessageEvent).data;
      if (!data || cancelled) return;
      const { payload } = JSON.parse(data) as ServerEvent;
      setError(payload.message as string);
    });

    return () => {
      cancelled = true;
      source.close();
      sourceRef.current = null;
    };
  }, [jobId]);

  return { job, streaming, error, finished };
}
